/**
 * 机场/飞行器日志（远程调试面板）的纯逻辑。
 *
 * 走的是大疆上云 API 的 fileupload 流程：
 *  1. `GET .../devices/{sn}/logs` 向机场实时查询当前可上传的日志文件列表；
 *  2. `POST .../devices/{sn}/logs` 让机场把选中的日志打包上传到对象存储；
 *  3. 机场通过 `fileupload_progress` 事件回报进度，完成后再取下载地址。
 *
 * 机场和飞行器是两个模块，各自有一份列表、一个 result 错误码、一个日志包，
 * 而且 boot_index 在两个模块里会重号，所以选中项一律用 `${module}:${boot_index}` 作键。
 * 这里只放不依赖运行时模块的纯函数，方便 `npm run test:dock-log` 直接跑。
 */

// 与 DOMAIN 枚举保持一致，避免为了一个常量把枚举（运行时值）引进来
export const DOCK_LOG_MODULE = '3'
export const DRONE_LOG_MODULE = '0'
export const DOCK_LOG_MODULES = [DOCK_LOG_MODULE, DRONE_LOG_MODULE]

export const DOCK_LOG_MODULE_LABEL: Record<string, string> = {
  [DOCK_LOG_MODULE]: '机场',
  [DRONE_LOG_MODULE]: '飞行器',
}

// 机场一次能列出几百段日志（实测 Dock3 有 416 段、每段约 600MB），
// 默认每个模块只铺最新的一批，其余折叠起来，导出体积过大时再确认一次。
export const DOCK_LOG_VISIBLE_LIMIT = 20
export const DOCK_LOG_LARGE_EXPORT_BYTES = 2 * 1024 * 1024 * 1024

/** 单个日志文件（一次开机产生的日志片段） */
export interface DockLogFileItem {
  boot_index: number
  start_time: number
  end_time: number
  size: number
}

/** 按模块（机场/飞行器）分组的日志文件信息 */
export interface DockLogModuleFiles {
  device_sn: string
  module: string
  object_key?: string
  file_id?: string
  result?: number
  list?: DockLogFileItem[] | null
}

/** 面板里一个模块的分组：列表 + 该模块自己的错误码 */
export interface DockLogGroup {
  module: string
  label: string
  deviceSn: string
  entries: DockLogFileItem[]
  result: number
  message: string
  // 机场忙的时候回的是空列表 + 错误码，这时候展示的是上一次读到的列表
  stale: boolean
}

export interface DockLogUploadFile {
  device_sn: string
  module: string
  list: DockLogFileItem[]
}

export interface DockLogUploadBody {
  device_sn: string
  happen_time: number
  logs_information: string
  files: DockLogUploadFile[]
}

/** 上传记录状态，与后端 DeviceLogsStatusEnum 一致 */
export enum DockLogStatus {
  Uploading = 1,
  Done = 2,
  Canceled = 3,
  Failed = 4,
}

export interface DockLogProgressFile {
  device_sn?: string
  device_model_domain?: string
  progress?: number
  result?: number
  upload_rate?: number
  // 列表接口回的是 `file_uploading` 这样的字符串，历史代码里也存过映射后的数字
  status?: string | number
}

/** `fileupload_progress` 推送的负载 */
export interface DockLogProgressPayload {
  sn?: string
  bid?: string
  output?: {
    logs_id?: string
    status?: string
    files?: DockLogProgressFile[]
  } | null
  result?: number
}

/** 上传记录（列表接口返回的行），status 取值见 DockLogStatus */
export interface DockLogRecord {
  logs_id: string
  create_time?: string
  status: number
  logs_information?: string
  logs_progress?: DockLogProgressFile[] | null
  device_logs?: {
    files?: DockLogModuleFiles[] | null
  } | null
}

/** 记录里一个可下载的日志包 */
export interface DockLogDownload {
  module: string
  label: string
  fileId: string
}

const WS_STATUS_TO_STATUS: Record<string, DockLogStatus> = {
  file_pull: DockLogStatus.Uploading,
  file_zip: DockLogStatus.Uploading,
  file_uploading: DockLogStatus.Uploading,
  ok: DockLogStatus.Done,
  canceled: DockLogStatus.Canceled,
  failed: DockLogStatus.Failed,
  timeout: DockLogStatus.Failed,
}

// CommonErrorEnum.STATUS_NOT_SUPPORTED + LogErrorCodeEnum，翻成面板里能直接读的话
const RESULT_MESSAGE: Record<number, string> = {
  // 实测：机场在打包上传日志、或有 DRC 手动控制 / 飞行任务在进行时都会回这个码，
  // 结束后会自己恢复，不需要重启机场
  314000: '机场正在上传日志或处于飞行/手动控制中，结束后会自动恢复',
  324001: '设备重启中断了日志导出',
  324012: '日志压缩超时，选择的日志过多，请减少后重试',
  // 后端在拿不到设备应答（离线、超时）时也用这个码
  324013: '暂时无法从机场获取最新日志列表，请稍后重试',
  324014: '设备日志列表为空，可刷新或重启机场后重试',
  324015: '飞行器未开机或未连接，请先远程开机并确认飞行器在舱内',
  324016: '机场存储空间不足，无法压缩日志',
  324017: '无法获取所选飞行器的日志，可刷新或重启机场后重试',
  324018: '日志压缩失败，请稍后重试或重启机场',
  324019: '机场网络异常，日志上传失败，请稍后重试',
}

/** 把模块返回的 result 翻成提示语；0 表示没问题 */
export function describeDockLogResult (result?: number | null): string {
  if (!result) {
    return ''
  }
  return RESULT_MESSAGE[result] || `设备返回错误码 ${result}`
}

/** 选中项的键：boot_index 在机场和飞行器之间会重号 */
export function dockLogEntryKey (module: string, bootIndex: number): string {
  return `${module}:${bootIndex}`
}

/** 把 `fileupload_progress` 的字符串状态翻译成记录状态 */
export function mapDockLogWsStatus (status?: string | null): DockLogStatus | null {
  if (!status) {
    return null
  }
  const mapped = WS_STATUS_TO_STATUS[status]
  return mapped === undefined ? null : mapped
}

/** 终态：不再有进度推送，可以去取下载地址了 */
export function isDockLogStatusEnd (status?: number | null): boolean {
  return status === DockLogStatus.Done || status === DockLogStatus.Canceled || status === DockLogStatus.Failed
}

/** 体积超过阈值的导出值得先确认一次：机场打包上传会占满上行带宽 */
export function isLargeDockLogExport (size: number, threshold: number = DOCK_LOG_LARGE_EXPORT_BYTES): boolean {
  return (size || 0) > threshold
}

/** 从实时日志返回里取出指定模块的那一组 */
export function pickDockLogModule (
  files?: DockLogModuleFiles[] | null,
  module: string = DOCK_LOG_MODULE
): DockLogModuleFiles | null {
  if (!files || files.length === 0) {
    return null
  }
  return files.find(file => file && String(file.module) === module) || null
}

/** 按开始时间倒序（新的在前），机场返回顺序并不保证 */
export function sortDockLogEntries (list?: DockLogFileItem[] | null): DockLogFileItem[] {
  if (!list || list.length === 0) {
    return []
  }
  return list.slice().sort((a, b) => {
    const diff = (b.start_time || 0) - (a.start_time || 0)
    return diff !== 0 ? diff : (b.boot_index || 0) - (a.boot_index || 0)
  })
}

/**
 * 合并上一次和这一次的列表。
 *
 * 机场在忙（打包上传日志、DRC 手动控制、执行航线）时 fileupload_list 会回
 * 「空列表 + 错误码」，直接覆盖就会把用户正看着的列表清空，等它闲下来才回得来。
 * 所以忙的时候保留上一次读到的列表，只把错误码带上，页面标成缓存即可。
 */
export function mergeDockLogModuleFiles (
  previous?: DockLogModuleFiles[] | null,
  incoming?: DockLogModuleFiles[] | null,
  modules: string[] = DOCK_LOG_MODULES
): DockLogModuleFiles[] {
  const merged: DockLogModuleFiles[] = []
  modules.forEach(module => {
    const next = pickDockLogModule(incoming, module)
    const prev = pickDockLogModule(previous, module)
    if (!next) {
      if (prev) {
        merged.push(prev)
      }
      return
    }
    const busy = (next.result || 0) !== 0
    const cached = prev?.list || []
    if (busy && (next.list || []).length === 0 && cached.length > 0) {
      merged.push({ ...prev as DockLogModuleFiles, result: next.result })
      return
    }
    merged.push(next)
  })
  return merged
}

/**
 * 每个模块都产出一组，即使机场没回这个模块也留个空壳：
 * 飞行器没开机时列表本来就是空的，要把原因写在分组里而不是让它凭空消失。
 */
export function buildDockLogGroups (
  files?: DockLogModuleFiles[] | null,
  modules: string[] = DOCK_LOG_MODULES
): DockLogGroup[] {
  return modules.map(module => {
    const moduleFiles = pickDockLogModule(files, module)
    const entries = sortDockLogEntries(moduleFiles?.list)
    const result = moduleFiles?.result || 0
    const label = DOCK_LOG_MODULE_LABEL[module] || module
    let message = describeDockLogResult(result)
    if (!message && entries.length === 0) {
      message = moduleFiles ? `${label}暂无可导出的日志` : `机场未返回${label}日志`
    }
    return {
      module,
      label,
      deviceSn: moduleFiles?.device_sn || '',
      entries,
      result,
      message,
      // 有列表又有错误码，说明这份列表是上一次读到的
      stale: entries.length > 0 && result !== 0,
    }
  })
}

/**
 * 这次读取是不是一个模块都没拿到新鲜数据（用来给自动刷新退避）。
 * 后端会在机场拒绝时回上一次缓存的列表并保留错误码，所以只看 result，不看有没有内容。
 */
export function isDockLogBusyResponse (files?: DockLogModuleFiles[] | null): boolean {
  if (!files || files.length === 0) {
    return false
  }
  return files.every(file => (file.result || 0) !== 0)
}

/** 选中项的条数与总大小，用来在工具栏上给个体感 */
export function summariseDockLogSelection (
  groups: DockLogGroup[] | null | undefined,
  selectedKeys: string[] | null | undefined
): { count: number, size: number } {
  if (!groups || !selectedKeys || selectedKeys.length === 0) {
    return { count: 0, size: 0 }
  }
  const keys = new Set(selectedKeys)
  return groups.reduce((acc, group) => {
    group.entries.forEach(entry => {
      if (keys.has(dockLogEntryKey(group.module, entry.boot_index))) {
        acc.count += 1
        acc.size += entry.size || 0
      }
    })
    return acc
  }, { count: 0, size: 0 })
}

/**
 * 组装上传请求体：每个有选中项的模块出一条 files 记录。
 * 选中项为空、或选中项都不在实时列表里时返回 null，由调用方决定提示什么。
 */
export function buildDockLogUploadBody (params: {
  deviceSn: string,
  groups?: DockLogGroup[] | null,
  selectedKeys?: string[] | null,
  happenTime?: number,
  information?: string,
}): DockLogUploadBody | null {
  const { deviceSn, groups, selectedKeys } = params
  if (!deviceSn || !groups || !selectedKeys || selectedKeys.length === 0) {
    return null
  }
  const keys = new Set(selectedKeys)
  const files: DockLogUploadFile[] = []
  groups.forEach(group => {
    const list = group.entries.filter(entry => keys.has(dockLogEntryKey(group.module, entry.boot_index)))
    if (list.length === 0) {
      return
    }
    files.push({
      // 用实时列表回报的 sn，而不是网关 sn：机场对两个模块各回各的
      device_sn: group.deviceSn || deviceSn,
      module: group.module,
      list,
    })
  })
  if (files.length === 0) {
    return null
  }
  return {
    device_sn: deviceSn,
    happen_time: params.happenTime || Date.now(),
    logs_information: params.information || '',
    files,
  }
}

/**
 * 把 `fileupload_progress` 推送合并进已有记录。
 * 返回被更新的记录（没有命中则返回 null），方便调用方判断要不要刷新列表。
 */
export function mergeDockLogProgress (
  records: DockLogRecord[] | null | undefined,
  payload?: DockLogProgressPayload | null
): DockLogRecord | null {
  const output = payload?.output
  if (!records || !output) {
    return null
  }
  const logsId = output.logs_id || payload?.bid
  if (!logsId) {
    return null
  }
  const record = records.find(item => item.logs_id === logsId)
  if (!record) {
    return null
  }
  const status = mapDockLogWsStatus(output.status)
  if (status !== null) {
    record.status = status
  }
  if (output.files && output.files.length > 0) {
    record.logs_progress = output.files.map(file => ({
      ...file,
      status: file.status,
    }))
  }
  return record
}

/** 一条记录的整体上传百分比（多个模块取平均），0 ~ 100 */
export function computeDockLogPercent (record?: DockLogRecord | null): number {
  const progress = record?.logs_progress
  if (!progress || progress.length === 0) {
    return 0
  }
  const total = progress.reduce((sum, file) => sum + (file.progress || 0), 0)
  const percent = Math.floor(total / progress.length)
  return Math.min(100, Math.max(0, percent))
}

/**
 * 记录里可下载的日志包。机场和飞行器各打各的包，上传完成后才有 file_id / object_key，
 * 所以一条记录可能给出两个下载项。
 */
export function findDockLogDownloads (
  record?: DockLogRecord | null,
  modules: string[] = DOCK_LOG_MODULES
): DockLogDownload[] {
  const files = record?.device_logs?.files
  if (!files || files.length === 0) {
    return []
  }
  const downloads: DockLogDownload[] = []
  modules.forEach(module => {
    const moduleFiles = pickDockLogModule(files, module)
    if (moduleFiles && moduleFiles.file_id && moduleFiles.object_key) {
      downloads.push({
        module,
        label: DOCK_LOG_MODULE_LABEL[module] || module,
        fileId: moduleFiles.file_id,
      })
    }
  })
  return downloads
}

/** 下载文件名：dock-log / drone-log + sn + 上传时间 */
export function buildDockLogFileName (deviceSn: string, createTime?: string, module: string = DOCK_LOG_MODULE): string {
  const prefix = module === DRONE_LOG_MODULE ? 'drone-log' : 'dock-log'
  const stamp = (createTime || '').replace(/[^0-9]/g, '')
  return `${prefix}-${deviceSn || 'unknown'}${stamp ? `-${stamp}` : ''}.tar`
}
